import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Grid } from '@material-ui/core';
import { Box, Activity, MapPin, Archive } from '@onward-delivery/design/icons';
import { typography } from '@/styles';

import { CEILINGS } from '../../constants';
import { Card } from '@onward-delivery/design/card';

import { GREY_TEXT, SectionHeader, SectionSubheader, iconCss } from './blocks';
import { DesignScope, TOKENS } from '../../blocks';

// Design `Card` ships unpadded — padding is the caller's.
const CARD_PADDING = 'p-[12px_14px]';

// Kept out of constants.js so that pure-data module stays importable from unit tests.
const CEILING_ICONS = { cubes: Box, weight: Activity, stops: MapPin, items: Archive };

const CEILING_ICON_SIZE = 28;

const ROW = css`
    margin-bottom: 20px;
    flex: 0;
    flex-basis: 0;
    gap: 12px;
    flex-wrap: nowrap;
`;

const CELL = css`
    flex: 1;
    min-width: 0;
`;

const CardLabel = styled.div`
    ${typography.sansSerif}

    font-size: 12px;
    font-weight: 600;
    color: #2b2b2b;
`;

const CardUnit = styled.div`
    ${typography.sansSerif}

    font-size: 11px;
    color: ${GREY_TEXT};
`;

// Bare input — MUI's outlined input is far too tall for this density.
const ValueInput = styled.input`
    ${typography.sansSerif}

    font-size: 16px;
    font-weight: 600;
    color: ${({ hasError }) => (hasError ? TOKENS.destructive : '#2b2b2b')};
    border: none;
    background: transparent;
    outline: none;
    width: 100%;
    padding: 2px 0 0;

    &::placeholder {
        color: #c4c4c4;
        font-weight: 400;
    }
`;

const FieldError = styled.div`
    ${typography.sansSerif}

    font-size: 11px;
    color: ${TOKENS.destructive};
    padding-top: 2px;
`;

// 2x2: cubes / weight on the first row, stops / pieces on the second.
const CEILING_ROWS = [CEILINGS.slice(0, 2), CEILINGS.slice(2, 4)];

const CeilingCard = ({ ceiling, updated, setUpdates, error }) => {
    const { label, unit, column, value } = ceiling;
    const Icon = CEILING_ICONS[value];
    const current = updated?.[column];

    return (
        <Card className={CARD_PADDING} tone="neutral">
            <Grid
                container
                direction="row"
                css={css`
                    align-items: center;
                    flex-wrap: nowrap;
                    gap: 12px;
                `}
            >
                <Grid item css={iconCss(GREY_TEXT, CEILING_ICON_SIZE)}>
                    <Icon />
                </Grid>
                <Grid item css={CELL}>
                    <CardLabel>
                        {label} <CardUnit as="span">({unit})</CardUnit>
                    </CardLabel>
                    <ValueInput
                        type="number"
                        min={1}
                        placeholder="Not set"
                        value={current ?? ''}
                        hasError={!!error}
                        onChange={(e) => {
                            const next = e.target.value === '' ? null : Number(e.target.value);
                            setUpdates((prev) => ({ ...prev, [column]: next }));
                        }}
                    />
                    {error ? <FieldError>{error}</FieldError> : null}
                </Grid>
            </Grid>
        </Card>
    );
};

export default function CeilingsSection({ updated, setUpdates, ceilingErrors }) {
    return (
        // All design components, so the scope sits at this section's root.
        <DesignScope>
            <SectionHeader>Ceilings</SectionHeader>
            <SectionSubheader>Set ceiling values below — the first one reached shuts off scheduling.</SectionSubheader>

            {CEILING_ROWS.map((row, i) => (
                <Grid key={i} direction="row" container css={ROW}>
                    {row.map((ceiling) => (
                        <Grid key={ceiling.value} item css={CELL}>
                            <CeilingCard
                                ceiling={ceiling}
                                updated={updated}
                                setUpdates={setUpdates}
                                error={ceilingErrors?.[ceiling.column]}
                            />
                        </Grid>
                    ))}
                </Grid>
            ))}
        </DesignScope>
    );
}
